// Personnel domain helpers — tier order, status enums, tier-average computation.

// Canonical five system tiers, in display order. Custom tiers (admin-added)
// append after these — see `getTiers()` which reads them off rawData.
export const TIERS = [
  "Managing Principal",
  "Principal",
  "Associate Principal",
  "Associate",
  "Architectural Designer",
];

// Returns [system tiers..., custom tiers...] — the effective list the app
// should render everywhere a tier picker or grouping exists.
export function getTiers(rawData) {
  const custom = rawData?.customTiers || [];
  return [...TIERS, ...custom.filter((t) => !TIERS.includes(t))];
}

export const STATUSES = ["active", "on leave", "departed"];

// The Personnel record backing a capacity row, if any — `linkedPersonId`
// first, then an exact name match. A row with none is a spreadsheet-import
// leftover (no rate card, no login) and is the only kind that may be removed
// from the capacity matrix / leave grid.
export function personnelRecordFor(rawData, person) {
  const personnel = rawData?.personnel || [];
  const name = (person?.name || "").trim().toLowerCase();
  return (
    personnel.find((r) => r.linkedPersonId && r.linkedPersonId === person?.id) ||
    personnel.find((r) => name && (r.fullName || "").trim().toLowerCase() === name) ||
    null
  );
}

// Capacity rows (`data.people`) carry the tier that prices every allocation.
// When a row has none — created before the rate card existed, or filed under a
// shortened name — fall back to the tier on the personnel record linked to it
// (`linkedPersonId`, else an exact name match). Without this the row prices at
// the flat $20,000 driver rate instead of its real tier average.
export function resolvedPeople(rawData) {
  const people = rawData?.people || [];
  if (!(rawData?.personnel || []).length) return people;
  return people.map((p) => {
    if (p.tier) return p;
    const rec = personnelRecordFor(rawData, p);
    return rec?.tier ? { ...p, tier: rec.tier } : p;
  });
}

// Rates for resourcing costs. Prefers the SERVER-computed averages
// (rawData.rates — available to principals, who never receive the raw
// personnel list), falling back to computing from rawData.personnel.
// Returns { tiers: {tier: {count, avgMonthly, avgHourly}}, firm: {...} }.
export function ratesFromData(rawData) {
  if (rawData?.rates?.firm) return rawData.rates;
  const personnel = rawData?.personnel || [];
  return { tiers: tierAverages(personnel), firm: firmAverages(personnel) };
}

// Firm-wide averages. Departed people never count.
export function firmAverages(personnel) {
  const out = { count: 0, avgMonthly: 0, avgHourly: 0 };
  for (const p of personnel || []) {
    if (p.status === "departed") continue;
    out.count += 1;
    out.avgMonthly += Number(p.monthlyRate || 0);
    out.avgHourly  += Number(p.hourlyRate  || 0);
  }
  if (out.count > 0) {
    out.avgMonthly /= out.count;
    out.avgHourly  /= out.count;
  }
  return out;
}

// For each tier, returns { count, avgMonthly, avgHourly }.
// Departed people never count. Buckets are pre-allocated for the canonical
// five plus any admin-added tiers passed in `extraTiers` — so an empty
// custom tier still shows up with count 0 (rather than undefined).
export function tierAverages(personnel, extraTiers = []) {
  const all = [...TIERS, ...extraTiers.filter((t) => t && !TIERS.includes(t))];
  const out = Object.fromEntries(all.map((t) => [t, { count: 0, avgMonthly: 0, avgHourly: 0 }]));
  for (const p of personnel || []) {
    if (p.status === "departed") continue;
    const bucket = out[p.tier] || (out[p.tier] = { count: 0, avgMonthly: 0, avgHourly: 0 });
    bucket.count += 1;
    bucket.avgMonthly += Number(p.monthlyRate || 0);
    bucket.avgHourly  += Number(p.hourlyRate  || 0);
  }
  for (const t of Object.keys(out)) {
    const b = out[t];
    if (b.count > 0) {
      b.avgMonthly = b.avgMonthly / b.count;
      b.avgHourly  = b.avgHourly  / b.count;
    }
  }
  return out;
}
